import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowLeft, Phone, CheckCircle, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { SERVICES_DATA } from "@/data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fade = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES_DATA.find(s => s.slug === slug);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", email: "", company: "", service: "", details: "" });
  const u = (k, v) => setF(p => ({ ...p, [k]: v }));

  useEffect(() => {
    window.scrollTo(0, 0);
    if (service) setF(p => ({ ...p, service: service.name }));
  }, [slug, service]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone) { toast.error("Name and phone are required."); return; }
    setSending(true);
    try {
      await axios.post(`${API}/audit`, { business_name: f.company || f.name, contact_name: f.name, email: f.email, phone: f.phone, industry: f.service, goals: f.details, challenges: "", current_marketing: "", monthly_budget: "", team_size: "", target_audience: "", competitors: "", additional_info: `Service page: ${f.service}`, website_url: "", instagram: "", facebook: "", linkedin: "", twitter: "", youtube: "" });
      setSent(true); toast.success("You're on the waitlist."); window.scrollTo(0, 0);
    } catch { toast.error("Something went wrong. WhatsApp us instead."); }
    setSending(false);
  };

  if (!service) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-6 px-6">
      <p className="text-xl tracking-tight">Service not found</p>
      <Link to="/" className="text-zinc-400 hover:text-white text-xs tracking-wider">Back to home</Link>
    </div>
  );

  if (sent) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center max-w-md">
        <CheckCircle className="w-10 h-10 text-white mx-auto" />
        <h1 className="text-2xl md:text-3xl tracking-tight mt-8">You're on the waitlist.</h1>
        <p className="text-zinc-400 text-xs mt-4 tracking-wider">We review every application personally. If there's a fit, we'll reach out within 48 hours.</p>
        <a href="https://wa.me/919041344420?text=Hi%20Prospera%2C%20I%27m%20interested%20in%20your%20services.%20Can%20we%20have%20a%20quick%20call%3F" target="_blank" rel="noopener noreferrer"
          className="mt-8 inline-flex items-center gap-3 bg-white text-black px-8 py-4 text-xs tracking-[0.2em] hover:bg-zinc-200 transition-colors">
          Or WhatsApp us directly <ArrowRight className="w-4 h-4" />
        </a>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white">
      {/* HERO */}
      <section className="pt-32 pb-16 px-6 md:px-16 lg:px-28">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] text-zinc-500 hover:text-white transition-colors mb-10 tracking-[0.2em]">
          <ArrowLeft className="w-3 h-3" /> Home
        </Link>
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">{service.name}</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.1] max-w-5xl">{service.tagline}</h1>
        </motion.div>
      </section>

      {/* PROBLEM */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">The problem</p>
        <h2 className="text-2xl md:text-3xl tracking-tight">If this sounds familiar, you're in the right place.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-12 bg-white/10">
          {service.problem.map((p, i) => (
            <div key={i} className="bg-black p-10">
              <p className="text-sm tracking-wider">{p.title}</p>
              <p className="text-zinc-500 text-[10px] mt-4 leading-relaxed tracking-wider">{p.desc}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* WHAT WE DO */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">What we do</p>
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-2xl">Here's exactly how we handle {service.name.toLowerCase()}.</h2>
        <div className="mt-12 space-y-4 max-w-3xl">
          {service.what_we_do.map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <CheckCircle className="w-3 h-3 text-zinc-500 mt-1 flex-shrink-0" />
              <p className="text-xs text-zinc-300 tracking-wider leading-relaxed">{item}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* PROCESS */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">How we work</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
          {service.process.map(s => (
            <div key={s.step} className="bg-black p-10">
              <p className="text-zinc-700 text-xl">{s.step}</p>
              <p className="text-sm tracking-wider mt-4">{s.title}</p>
              <p className="text-zinc-500 text-[10px] mt-3 leading-relaxed tracking-wider">{s.desc}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* GEO REACH */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Where we operate</p>
        <h2 className="text-xl md:text-2xl tracking-tight max-w-3xl">
          {service.name} for businesses in every major city. Locally sharp. Globally connected.
        </h2>
        <p className="text-zinc-500 text-[10px] mt-6 tracking-wider max-w-2xl leading-relaxed">
          Whether you're a startup in Chandigarh, a D2C brand in Mumbai, an enterprise in Dubai, or scaling in Toronto — our {service.name.toLowerCase()} team operates across time zones. We've worked with businesses in New York, Los Angeles, Chicago, San Francisco, Houston, London, Sydney, Melbourne, and every Indian metro. Same quality. Same commitment. Your city doesn't limit your growth.
        </p>
        <div className="flex flex-wrap gap-2 mt-8">
          {["New York", "Los Angeles", "Chicago", "San Francisco", "Houston", "Miami", "Dallas", "Seattle", "Austin", "Boston", "Washington DC", "Toronto", "Vancouver", "Montreal", "Sydney", "Melbourne", "Brisbane", "Perth", "London", "Dubai", "Abu Dhabi", "Mumbai", "Delhi NCR", "Bangalore", "Chennai", "Hyderabad", "Pune", "Chandigarh", "Kolkata", "Jaipur", "Ahmedabad"].map(city => (
            <span key={city} className="text-[10px] text-zinc-600 border border-white/5 px-3 py-1 tracking-wider">{service.name} {city}</span>
          ))}
        </div>
      </motion.section>

      {/* CASE STUDY */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Learning from the best</p>
        <div className="bg-[#080808] border border-white/10 p-8 md:p-12 max-w-3xl">
          <Lightbulb className="w-4 h-4 text-zinc-600" />
          <p className="text-xs tracking-wider mt-4">{service.case_study.brand}</p>
          <p className="text-zinc-400 text-[10px] mt-4 leading-relaxed tracking-wider">{service.case_study.lesson}</p>
          <p className="text-zinc-300 text-[10px] mt-4 leading-relaxed tracking-wider">{service.case_study.takeaway}</p>
        </div>
      </motion.section>

      {/* FAQ */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Questions</p>
        <div className="max-w-3xl">
          <Accordion type="single" collapsible>
            {service.faqs.map((f, i) => (
              <AccordionItem key={i} value={`sf-${i}`} className="border-white/10">
                <AccordionTrigger className="text-left text-white hover:no-underline text-xs tracking-wider py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-zinc-400 text-[10px] leading-relaxed tracking-wider pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </motion.section>

      {/* WAITLIST CTA */}
      <section className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <motion.div variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Join the waitlist</p>
          <h2 className="text-2xl md:text-3xl tracking-tight max-w-2xl">
            We work with a limited number of clients at a time.
          </h2>
          <p className="text-zinc-500 text-xs mt-4 tracking-wider max-w-xl leading-relaxed">
            Not because we can't handle more — because we won't compromise on results. If you're serious about growth and care about outcomes more than invoices, we'd like to hear from you.
          </p>
        </motion.div>
        <form onSubmit={submit} className="mt-12 max-w-xl space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Name *</label>
              <Input data-testid="wl-name" value={f.name} onChange={e => u("name", e.target.value)} placeholder="Your name"
                className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Phone *</label>
              <Input data-testid="wl-phone" value={f.phone} onChange={e => u("phone", e.target.value)} placeholder="+91 XXXXX XXXXX"
                className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Email</label>
              <Input data-testid="wl-email" value={f.email} onChange={e => u("email", e.target.value)} placeholder="you@company.com" type="email"
                className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Company / brand</label>
              <Input data-testid="wl-company" value={f.company} onChange={e => u("company", e.target.value)} placeholder="Your business"
                className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Tell us what you need</label>
            <Textarea data-testid="wl-details" value={f.details} onChange={e => u("details", e.target.value)} rows={3}
              placeholder="What's happening in your business? What are you trying to achieve?"
              className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 rounded-none text-xs tracking-wider resize-none" />
          </div>
          <Button type="submit" disabled={sending} data-testid="wl-submit"
            className="bg-white text-black hover:bg-zinc-200 rounded-none px-10 py-4 text-xs tracking-[0.2em] h-auto disabled:opacity-50 w-full md:w-auto">
            {sending ? "Submitting..." : "Join the waitlist"} {!sending && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </form>
      </section>

      {/* SEO */}
      <section className="py-12 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <div className="flex flex-wrap gap-2">
          {service.seo_keywords.map(kw => (
            <span key={kw} className="text-[10px] text-zinc-700 border border-white/5 px-3 py-1 tracking-wider">{kw}</span>
          ))}
        </div>
      </section>
    </div>
  );
}
